import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  growthExperimentEngineApi,
  type AcceptanceCriterion,
  type GrowthRecommendation,
  type APISpecification,
  type AttributionFactor,
  type BusinessObjective,
  type CapabilityModule,
  type DatabaseTable,
  type EngineDashboard,
  type EngineValidation,
  type MGELPRDStatus,
  type PhilosophyQuestion,
  type OpportunityDomain,
  type HypothesisField,
  type ExperimentType,
  type ObservationMetric,
  type StatisticalMetric,
  type LearningTarget,
  type KnowledgeCategory,
  type RankingFactor,
  type ExecutiveMetric,
  type PrivacyControl,
  type PerformanceTarget,
  type TestingRequirement,
  type UXCapability,
} from '../../api/growth-experiment-engine-prd';

type Tab =
  | 'overview'
  | 'philosophy'
  | 'capabilities'
  | 'opportunities'
  | 'hypothesis'
  | 'experiments'
  | 'observation'
  | 'attribution'
  | 'learning'
  | 'executive'
  | 'recommendations'
  | 'ux'
  | 'engineering'
  | 'status';
type Theme = 'light' | 'dark';

const riskColors: Record<string, string> = {
  Low: 'bg-violet-100 text-violet-800 dark:bg-violet-900 dark:text-violet-200',
  Medium: 'bg-indigo-100 text-indigo-800 dark:bg-indigo-900 dark:text-indigo-200',
  High: 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200',
};

export function GrowthExperimentEnginePRDPage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<EngineDashboard | null>(null);
  const [objectives, setObjectives] = useState<BusinessObjective[]>([]);
  const [philosophy, setPhilosophy] = useState<PhilosophyQuestion[]>([]);
  const [capabilities, setCapabilities] = useState<CapabilityModule[]>([]);
  const [opportunities, setOpportunities] = useState<OpportunityDomain[]>([]);
  const [hypothesisFields, setHypothesisFields] = useState<HypothesisField[]>([]);
  const [experimentTypes, setExperimentTypes] = useState<ExperimentType[]>([]);
  const [observationMetrics, setObservationMetrics] = useState<ObservationMetric[]>([]);
  const [attribution, setAttribution] = useState<AttributionFactor[]>([]);
  const [statistical, setStatistical] = useState<StatisticalMetric[]>([]);
  const [learning, setLearning] = useState<LearningTarget[]>([]);
  const [knowledge, setKnowledge] = useState<KnowledgeCategory[]>([]);
  const [ranking, setRanking] = useState<RankingFactor[]>([]);
  const [executive, setExecutive] = useState<ExecutiveMetric[]>([]);
  const [privacy, setPrivacy] = useState<PrivacyControl[]>([]);
  const [recommendations, setRecommendations] = useState<GrowthRecommendation[]>([]);
  const [uxCaps, setUxCaps] = useState<UXCapability[]>([]);
  const [perf, setPerf] = useState<PerformanceTarget[]>([]);
  const [apis, setApis] = useState<APISpecification[]>([]);
  const [tables, setTables] = useState<DatabaseTable[]>([]);
  const [testing, setTesting] = useState<TestingRequirement[]>([]);
  const [acceptance, setAcceptance] = useState<AcceptanceCriterion[]>([]);
  const [status, setStatus] = useState<MGELPRDStatus | null>(null);
  const [validation, setValidation] = useState<EngineValidation | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const results = await Promise.all([
        growthExperimentEngineApi.dashboard(),
        growthExperimentEngineApi.businessObjectives(),
        growthExperimentEngineApi.philosophy(),
        growthExperimentEngineApi.capabilityArchitecture(),
        growthExperimentEngineApi.opportunityDetection(),
        growthExperimentEngineApi.hypothesisEngine(),
        growthExperimentEngineApi.experimentBuilder(),
        growthExperimentEngineApi.observationEngine(),
        growthExperimentEngineApi.attribution(),
        growthExperimentEngineApi.statisticalEvaluation(),
        growthExperimentEngineApi.continuousLearning(),
        growthExperimentEngineApi.knowledgeEvolution(),
        growthExperimentEngineApi.recommendationRanking(),
        growthExperimentEngineApi.executiveDashboard(),
        growthExperimentEngineApi.privacyControls(),
        growthExperimentEngineApi.recommendations(),
        growthExperimentEngineApi.uxCapabilities(),
        growthExperimentEngineApi.performanceTargets(),
        growthExperimentEngineApi.apis(),
        growthExperimentEngineApi.databaseSchema(),
        growthExperimentEngineApi.testing(),
        growthExperimentEngineApi.acceptanceCriteria(),
        growthExperimentEngineApi.status(),
      ]);
      setDashboard(results[0]);
      setObjectives(results[1]);
      setPhilosophy(results[2]);
      setCapabilities(results[3]);
      setOpportunities(results[4]);
      setHypothesisFields(results[5]);
      setExperimentTypes(results[6]);
      setObservationMetrics(results[7]);
      setAttribution(results[8]);
      setStatistical(results[9]);
      setLearning(results[10]);
      setKnowledge(results[11]);
      setRanking(results[12]);
      setExecutive(results[13]);
      setPrivacy(results[14]);
      setRecommendations(results[15]);
      setUxCaps(results[16]);
      setPerf(results[17]);
      setApis(results[18]);
      setTables(results[19]);
      setTesting(results[20]);
      setAcceptance(results[21]);
      setStatus(results[22]);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load Growth Experiment Engine PRD');
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const validate = async () => {
    setBusy(true);
    setActionMsg(null);
    try {
      const result = await growthExperimentEngineApi.validate({
        attrs: {
          opportunity_ready: true,
          hypothesis_ready: true,
          experiment_ready: true,
          observation_ready: true,
          attribution_ready: true,
          statistical_ready: true,
          learning_ready: true,
          privacy_ready: true,
          performance_met: true,
          acceptance_passed: true,
        },
      });
      setValidation(result);
      setActionMsg(
        result.valid
          ? 'Growth Experiment Engine PRD passes production readiness gate'
          : `Gaps: ${result.gaps?.join(', ')}`,
      );
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Validation failed');
    } finally {
      setBusy(false);
    }
  };

  const card = dark ? 'border-violet-900/40 bg-slate-900' : 'border-violet-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-500';
  const shell = dark ? 'bg-slate-950 text-slate-100' : 'bg-violet-50/30 text-slate-900';
  const accent = dark ? 'text-violet-300' : 'text-violet-700';
  const tabActive = 'bg-violet-600 text-white';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Overview' },
    { id: 'philosophy', label: 'Philosophy' },
    { id: 'capabilities', label: 'Capabilities' },
    { id: 'opportunities', label: 'Opportunities' },
    { id: 'hypothesis', label: 'Hypothesis' },
    { id: 'experiments', label: 'Experiments' },
    { id: 'observation', label: 'Observation' },
    { id: 'attribution', label: 'Attribution' },
    { id: 'learning', label: 'Learning' },
    { id: 'executive', label: 'Executive' },
    { id: 'recommendations', label: 'Recommendations' },
    { id: 'ux', label: 'UX' },
    { id: 'engineering', label: 'Engineering' },
    { id: 'status', label: 'Status' },
  ];

  return (
    <div className={`space-y-6 rounded-xl p-1 ${shell}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>PRD-000008</p>
          <h1 className="text-2xl font-semibold">Growth Experiment & Continuous Learning</h1>
          <p className={`text-sm ${muted}`}>
            Scientific optimization, attribution intelligence, experiment orchestration, AI learning framework and
            knowledge evolution
          </p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            <Link to="/platform/profit-intelligence-leakage-engine-prd" className={`${accent} hover:underline`}>
              PRD-000007
            </Link>
            <span className={muted}>→</span>
            <Link to="/platform/fba-growth-engine-prd" className={`${accent} hover:underline`}>
              PRD-000006
            </Link>
          </div>
        </div>
        <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg border px-3 py-1.5 text-sm ${card}`}>
          {dark ? 'Light' : 'Dark'}
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">{error}</div>
      )}
      {actionMsg && (
        <div className="rounded-lg border border-violet-300 bg-violet-50 p-3 text-sm text-violet-700 dark:border-violet-800 dark:bg-violet-950 dark:text-violet-300">{actionMsg}</div>
      )}

      <div className="flex flex-wrap gap-2">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? tabActive : `${card} border`}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-5 ${card}`}>
            <h2 className="text-lg font-semibold">Scientific Learning Core</h2>
            <p className={`mt-1 text-sm ${muted}`}>Depends on: {dashboard.depends_on.join(' · ')}</p>
            <p className={`mt-3 text-sm ${muted}`}>
              Not merely executing experiments — continuously improving every business recommendation through measurable
              evidence and accumulated learning. Every optimization becomes organizational knowledge. No recommendation
              disappears without producing learning.
            </p>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Capability Modules', value: dashboard.capability_modules },
              { label: 'Opportunity Domains', value: dashboard.opportunity_domains },
              { label: 'Hypothesis Fields', value: dashboard.hypothesis_fields },
              { label: 'Experiment Types', value: dashboard.experiment_types },
              { label: 'Attribution Factors', value: dashboard.attribution_factors },
              { label: 'Learning Targets', value: dashboard.learning_targets },
              { label: 'Privacy Controls', value: dashboard.privacy_controls },
              { label: 'Acceptance Criteria', value: dashboard.acceptance_criteria },
            ].map((m) => (
              <div key={m.label} className={`rounded-xl border p-4 ${card}`}>
                <p className={`text-xs uppercase ${muted}`}>{m.label}</p>
                <p className="mt-1 text-2xl font-semibold">{m.value}</p>
              </div>
            ))}
          </div>
          <div className={`rounded-xl border p-5 ${card}`}>
            <h3 className="font-semibold">Business Objectives</h3>
            <div className="mt-3 grid gap-2 md:grid-cols-2 lg:grid-cols-3">
              {objectives.map((o) => (
                <div key={o.key} className="rounded-lg border border-violet-200 p-3 dark:border-violet-800">
                  <span className="font-medium">{o.name}</span>
                  <p className={`mt-1 text-xs ${muted}`}>{o.description}</p>
                  {o.kpi && <span className="mt-1 inline-block rounded bg-violet-100 px-2 py-0.5 text-xs dark:bg-violet-900">{o.kpi}</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'philosophy' && (
        <div className="space-y-3">
          {philosophy.map((q, i) => (
            <div key={q.key} className={`flex items-start gap-4 rounded-xl border p-4 ${card}`}>
              <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-violet-600 text-sm font-bold text-white">{i + 1}</span>
              <div>
                <h3 className="font-semibold">{q.question}</h3>
                {q.description && <p className={`mt-1 text-sm ${muted}`}>{q.description}</p>}
                {q.required && <span className="mt-2 inline-block rounded bg-indigo-100 px-2 py-0.5 text-xs dark:bg-indigo-900">Required</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'capabilities' && (
        <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
          {capabilities.sort((a, b) => a.order - b.order).map((c) => (
            <div key={c.key} className={`rounded-xl border p-4 ${card}`}>
              <span className="text-xs text-violet-600 dark:text-violet-400">{c.order}</span>
              <h3 className="font-semibold">{c.name}</h3>
              {c.description && <p className={`mt-1 text-sm ${muted}`}>{c.description}</p>}
              {c.integrations && c.integrations.length > 0 && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {c.integrations.map((i) => (
                    <span key={i} className="rounded bg-indigo-100 px-2 py-0.5 text-xs dark:bg-indigo-900">{i}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'opportunities' && (
        <div className="grid gap-3 md:grid-cols-2">
          {opportunities.map((o) => (
            <div key={o.key} className={`rounded-xl border p-4 ${card}`}>
              <h3 className="font-semibold">{o.name}</h3>
              {o.description && <p className={`mt-1 text-sm ${muted}`}>{o.description}</p>}
              {o.expected_impact && <p className={`mt-2 text-sm font-medium ${accent}`}>Expected impact: {o.expected_impact}</p>}
              {o.sample_signals && (
                <ul className={`mt-2 list-inside list-disc text-xs ${muted}`}>
                  {o.sample_signals.map((s) => <li key={s}>{s}</li>)}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'hypothesis' && (
        <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
          {hypothesisFields.map((f) => (
            <div key={f.key} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">{f.name}</h3>
                {f.required && <span className="rounded bg-violet-100 px-2 py-0.5 text-xs dark:bg-violet-900">Required</span>}
              </div>
              {f.description && <p className={`mt-1 text-sm ${muted}`}>{f.description}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'experiments' && (
        <div className="grid gap-3 md:grid-cols-2">
          {experimentTypes.map((e) => (
            <div key={e.key} className={`rounded-xl border p-4 ${card}`}>
              <h3 className="font-semibold">{e.name}</h3>
              {e.description && <p className={`mt-1 text-sm ${muted}`}>{e.description}</p>}
              {e.scopes && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {e.scopes.map((s) => (
                    <span key={s} className="rounded border px-2 py-0.5 text-xs">{s}</span>
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'observation' && (
        <div className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
            {observationMetrics.map((m) => (
              <div key={m.key} className={`rounded-xl border p-4 ${card}`}>
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold">{m.name}</h3>
                  {m.timeline && <span className="rounded bg-indigo-100 px-2 py-0.5 text-xs dark:bg-indigo-900">Timeline</span>}
                </div>
                {m.category && <p className={`text-xs ${accent}`}>{m.category}</p>}
                {m.description && <p className={`mt-1 text-sm ${muted}`}>{m.description}</p>}
              </div>
            ))}
          </div>
          <div className={`rounded-xl border p-5 ${card}`}>
            <h3 className="font-semibold">Statistical Evaluation</h3>
            <div className="mt-3 grid gap-2 md:grid-cols-2">
              {statistical.map((s) => (
                <div key={s.key} className="rounded-lg border border-violet-200 p-3 dark:border-violet-800">
                  <span className="font-medium">{s.name}</span>
                  {s.description && <p className={`text-xs ${muted}`}>{s.description}</p>}
                  {s.threshold && <p className={`mt-1 text-xs ${accent}`}>Threshold: {s.threshold}</p>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'attribution' && (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {attribution.map((a) => (
            <div key={a.key} className={`rounded-xl border p-4 ${card}`}>
              <div className="flex items-center justify-between">
                <h3 className="font-semibold">{a.name}</h3>
                <span className={`rounded px-2 py-0.5 text-xs ${a.measured ? 'bg-violet-100 dark:bg-violet-900' : 'bg-indigo-100 dark:bg-indigo-900'}`}>
                  {a.measured ? 'Measured' : 'Modeled'}
                </span>
              </div>
              <p className={`text-xs ${muted}`}>{a.source_type}</p>
              {a.description && <p className={`mt-1 text-sm ${muted}`}>{a.description}</p>}
              {a.metrics && (
                <div className="mt-2 flex flex-wrap gap-1">
                  {a.metrics.map((m) => <span key={m} className="rounded border px-2 py-0.5 text-xs">{m}</span>)}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'learning' && (
        <div className="space-y-4">
          <section>
            <h2 className="mb-3 text-lg font-semibold">Continuous Learning Targets</h2>
            <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
              {learning.map((l) => (
                <div key={l.key} className={`rounded-xl border p-4 ${card}`}>
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold">{l.name}</h3>
                    {l.versioned && <span className="rounded bg-violet-100 px-2 py-0.5 text-xs dark:bg-violet-900">Versioned</span>}
                  </div>
                  {l.description && <p className={`mt-1 text-sm ${muted}`}>{l.description}</p>}
                </div>
              ))}
            </div>
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">Knowledge Evolution</h2>
            <div className="grid gap-2 md:grid-cols-2">
              {knowledge.map((k) => (
                <div key={k.key} className={`rounded-xl border p-4 ${card}`}>
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold">{k.name}</h3>
                    {k.auditable && <span className="rounded bg-indigo-100 px-2 py-0.5 text-xs dark:bg-indigo-900">Auditable</span>}
                  </div>
                  {k.description && <p className={`mt-1 text-sm ${muted}`}>{k.description}</p>}
                </div>
              ))}
            </div>
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">Recommendation Ranking Factors</h2>
            <div className="grid gap-2 md:grid-cols-2 lg:grid-cols-3">
              {ranking.map((r) => (
                <div key={r.key} className={`rounded-lg border px-4 py-3 ${card}`}>
                  <span className="font-medium">{r.name}</span>
                  {r.weight != null && <span className="ml-2 text-sm text-violet-600 dark:text-violet-400">{(r.weight * 100).toFixed(0)}%</span>}
                  {r.description && <p className={`text-xs ${muted}`}>{r.description}</p>}
                </div>
              ))}
            </div>
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">Privacy & Multi-Tenant Learning</h2>
            <div className="grid gap-2 md:grid-cols-2">
              {privacy.map((p) => (
                <div key={p.key} className={`rounded-xl border p-4 ${card}`}>
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold">{p.name}</h3>
                    {p.enforced && <span className="rounded bg-green-100 px-2 py-0.5 text-xs text-green-800 dark:bg-green-900 dark:text-green-200">Enforced</span>}
                  </div>
                  {p.description && <p className={`mt-1 text-sm ${muted}`}>{p.description}</p>}
                </div>
              ))}
            </div>
          </section>
        </div>
      )}

      {tab === 'executive' && (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {executive.map((m) => (
            <div key={m.key} className={`rounded-xl border p-4 ${card}`}>
              <h3 className="font-semibold">{m.name}</h3>
              {m.description && <p className={`mt-1 text-sm ${muted}`}>{m.description}</p>}
              {m.unit && <span className="mt-2 inline-block rounded bg-violet-100 px-2 py-0.5 text-xs dark:bg-violet-900">{m.unit}</span>}
            </div>
          ))}
        </div>
      )}

      {tab === 'recommendations' && (
        <div className="space-y-3">
          {recommendations.map((r) => (
            <div key={r.key} className={`rounded-xl border p-5 ${card}`}>
              <div className="flex flex-wrap items-start justify-between gap-2">
                <h3 className="font-semibold">{r.title}</h3>
                <div className="flex gap-2">
                  <span className="rounded bg-violet-100 px-2 py-0.5 text-xs font-medium dark:bg-violet-900">{(r.confidence * 100).toFixed(0)}%</span>
                  <span className={`rounded px-2 py-0.5 text-xs ${riskColors[r.risk] ?? ''}`}>{r.risk} risk</span>
                </div>
              </div>
              <p className={`mt-1 text-xs ${accent}`}>{r.business_objective}</p>
              <div className="mt-3 flex flex-wrap gap-3 text-sm">
                <span>Revenue: <strong>{r.expected_revenue}</strong></span>
                <span>Profit: <strong>{r.expected_profit}</strong></span>
                <span>Effort: {r.implementation_effort} · {r.observation_window}</span>
              </div>
              {r.learning_outcome && <p className={`mt-2 text-sm ${accent}`}>Learning: {r.learning_outcome}</p>}
              {r.evidence && r.evidence.length > 0 && (
                <ul className={`mt-2 list-inside list-disc text-xs ${muted}`}>
                  {r.evidence.map((ev) => <li key={ev}>{ev}</li>)}
                </ul>
              )}
            </div>
          ))}
        </div>
      )}

      {tab === 'ux' && (
        <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {uxCaps.map((u) => (
            <div key={u.key} className={`rounded-xl border p-4 ${card}`}>
              <h3 className="font-semibold">{u.name}</h3>
              {u.mode && <code className={`text-xs ${muted}`}>{u.mode}</code>}
              {u.description && <p className={`mt-1 text-sm ${muted}`}>{u.description}</p>}
            </div>
          ))}
        </div>
      )}

      {tab === 'engineering' && (
        <div className="space-y-6">
          <section>
            <h2 className="mb-3 text-lg font-semibold">Performance Targets</h2>
            <div className="grid gap-2 md:grid-cols-2">
              {perf.map((p) => (
                <div key={p.key} className={`rounded-lg border px-4 py-3 ${card}`}>
                  <span className="font-medium">{p.surface}</span>
                  <span className="ml-2 text-lg font-semibold text-violet-600 dark:text-violet-400">{p.max_value}{p.unit !== 'seconds' && p.unit !== 'enterprise' && p.unit !== 'progress_visible' ? '' : p.unit === 'enterprise' || p.unit === 'progress_visible' ? '' : ` ${p.unit}`}</span>
                </div>
              ))}
            </div>
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">APIs</h2>
            {apis.map((a) => (
              <div key={a.key} className={`mb-3 rounded-xl border p-4 ${card}`}>
                <h3 className="font-semibold capitalize">{a.category}: {a.key}</h3>
                <p className={`text-sm ${muted}`}>{a.description}</p>
                <ul className={`mt-2 list-inside list-disc text-xs ${muted}`}>
                  {a.endpoints?.map((e) => <li key={e}>{e}</li>)}
                </ul>
              </div>
            ))}
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">Database Schema</h2>
            <div className="grid gap-3 md:grid-cols-2">
              {tables.map((t) => (
                <div key={t.key} className={`rounded-xl border p-4 ${card}`}>
                  <h3 className="font-semibold">{t.name}</h3>
                  <p className={`text-sm ${muted}`}>{t.purpose}</p>
                </div>
              ))}
            </div>
          </section>
          <section>
            <h2 className="mb-3 text-lg font-semibold">Testing & Acceptance</h2>
            <div className="grid gap-4 md:grid-cols-2">
              <div>
                {testing.map((t) => (
                  <div key={t.key} className={`mb-2 rounded-lg border px-4 py-2 ${card}`}>
                    <span className="font-medium capitalize">{t.level}</span>
                    <span className={`ml-2 text-xs ${muted}`}>{t.scope}</span>
                  </div>
                ))}
              </div>
              <div>
                {acceptance.map((a) => (
                  <div key={a.key} className={`mb-2 rounded-lg border px-4 py-2 ${card}`}>
                    <p className="text-sm">{a.criterion}</p>
                    {a.target && <p className={`text-xs ${muted}`}>Target: {a.target}</p>}
                  </div>
                ))}
              </div>
            </div>
          </section>
        </div>
      )}

      {tab === 'status' && status && (
        <div className="space-y-4">
          <div className={`rounded-xl border p-5 ${card}`}>
            <span className="rounded-full bg-violet-100 px-3 py-1 text-sm font-medium text-violet-800 dark:bg-violet-900 dark:text-violet-200">{status.status}</span>
            {status.scientific_learning && <p className={`mt-3 text-sm ${muted}`}>Scientific learning core — every optimization produces organizational knowledge</p>}
            {status.production_ready && <p className={`text-sm ${muted}`}>Production implementation specification complete</p>}
          </div>
          <div className={`rounded-xl border p-5 ${card}`}>
            <h3 className="font-semibold">Validate Growth Experiment Engine PRD</h3>
            <button type="button" disabled={busy} onClick={validate} className={`mt-4 rounded-lg px-4 py-2 text-sm text-white ${tabActive} disabled:opacity-50`}>
              {busy ? 'Validating…' : 'Run Readiness Validation'}
            </button>
            {validation && (
              <pre className="mt-3 overflow-x-auto rounded-lg bg-slate-100 p-3 text-xs dark:bg-slate-800">{JSON.stringify(validation, null, 2)}</pre>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
